import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlagshipPortfoliosTab from "./components/FlagshipPortfoliosTab";
import { Download, Loader2, RefreshCw, Upload, X } from "lucide-react";
import AdminProductFilters from "./components/AdminProductFilters";
import AdminProductStatsHeader from "./components/AdminProductStatsHeader";
import AdminProductsTable from "./components/AdminProductsTable";
import CsvUploadReview from "./components/CsvUploadReview";
import ContractRemarksPopover from "./components/ContractRemarksPopover";
import type { RemarksPopoverTarget } from "./components/ContractRemarksPopover";
import { useAdminProductsList } from "./hooks/useAdminProductsList";
import { useAdminProductMetadata } from "./hooks/useAdminProductMetadata";
import { useAdminColumnFilters } from "./hooks/useAdminColumnFilters";
import { useToast } from "../../../contexts/ToastContext";
import { generateMetadataCsv, downloadCsv } from "./utils/metadataCsv";
import {
  METADATA_FIELD_CONFIGS,
  METADATA_TAG_CONFIGS,
  METADATA_TEXT_CONFIGS,
} from "../../../types/productMetadata";
import type { ProductMetadata } from "../../../types/productMetadata";
import type { Visibility } from "../../../types/subscription";
import type { AdminProductItem } from "./hooks/useAdminProductsList";

const TAG_KEYS = new Set<string>(METADATA_FIELD_CONFIGS.map((c) => c.key));

function getDataColumnValue(
  product: AdminProductItem,
  columnKey: string
): string | null {
  switch (columnKey) {
    case "type":
      return product.productType === "basket"
        ? "Basket"
        : product.productType === "portfolio"
        ? "Portfolio"
        : "Managed Strategy";
    case "provider":
      return product.providerName;
    case "visibility":
      return product.visibility;
    default:
      return null;
  }
}

const ProductsCatalog: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    products,
    loading,
    error,
    totalCount,
    basketCount,
    portfolioCount,
    managedStrategyCount,
    filterTab,
    setFilterTab,
    searchTerm,
    setSearchTerm,
    providerFilter,
    setProviderFilter,
    visibilityFilter,
    setVisibilityFilter,
    remarksFilter,
    setRemarksFilter,
    sortField,
    sortDesc,
    handleSort,
    providers,
    reload,
    updateVisibility,
    isVisibilitySaving,
    dataVersion,
  } = useAdminProductsList();

  const { getMetadata, isSaving, updateTag } = useAdminProductMetadata(dataVersion);

  const handleVisibilityChange = useCallback(
    async (product: AdminProductItem, next: Visibility) => {
      try {
        await updateVisibility(product, next);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Failed to update visibility. Please try again.";
        toast.error(message);
      }
    },
    [updateVisibility, toast]
  );

  const {
    columnFilters,
    toggleFilter,
    selectAllFilter,
    clearFilter,
    clearAllFilters,
    activeFilterCount,
  } = useAdminColumnFilters();

  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [remarksTarget, setRemarksTarget] = useState<RemarksPopoverTarget | null>(null);

  const handleRemarksClick = useCallback(
    (product: AdminProductItem, anchor: DOMRect) => setRemarksTarget({ product, anchor }),
    []
  );

  const filterOptions = useMemo(() => {
    const opts: Record<string, string[]> = {};

    const types = new Set<string>();
    const providerNames = new Set<string>();
    const visibilities = new Set<string>();
    for (const p of products) {
      types.add(
        p.productType === "basket" ? "Basket" : p.productType === "portfolio" ? "Portfolio" : "Managed Strategy"
      );
      providerNames.add(p.providerName);
      visibilities.add(p.visibility);
    }
    opts["type"] = [...types].sort();
    opts["provider"] = [...providerNames].sort();
    opts["visibility"] = [...visibilities].sort();

    for (const config of METADATA_TAG_CONFIGS) {
      opts[config.key] = ["", ...(config.options as unknown as string[])];
    }

    for (const config of METADATA_TEXT_CONFIGS) {
      const values = new Set<string>();
      for (const product of products) {
        const value = getMetadata(product)?.[config.key];
        if (value) values.add(value);
      }
      opts[config.key] = ["", ...Array.from(values).sort()];
    }

    return opts;
  }, [products, getMetadata]);

  const columnFilteredProducts = useMemo(() => {
    const activeKeys = Object.keys(columnFilters).filter(
      (k) => columnFilters[k]?.size > 0
    );
    if (activeKeys.length === 0) return products;

    const dataFilterKeys = activeKeys.filter((k) => !TAG_KEYS.has(k));
    const tagFilterKeys = activeKeys.filter((k) => TAG_KEYS.has(k));

    return products.filter((product) => {
      for (const key of dataFilterKeys) {
        const filterValues = columnFilters[key];
        const value = getDataColumnValue(product, key);
        if (value !== null && !filterValues.has(value)) return false;
      }

      if (tagFilterKeys.length > 0) {
        const metadata = getMetadata(product);
        for (const key of tagFilterKeys) {
          const filterValues = columnFilters[key];
          const tagValue =
            (metadata?.[key as keyof ProductMetadata] as string) ?? "";
          if (!filterValues.has(tagValue)) return false;
        }
      }

      return true;
    });
  }, [products, columnFilters, getMetadata]);

  const handleDownloadCsv = useCallback(() => {
    const csv = generateMetadataCsv(products, getMetadata);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `product-metadata-${date}.csv`);
  }, [products, getMetadata]);

  const handleCsvUploadComplete = useCallback(() => {
    setShowCsvUpload(false);
    reload();
  }, [reload]);

  const handleProductClick = (product: AdminProductItem) => {
    if (product.productType === "managedStrategy") {
      navigate(`/admin/products/managed-strategies/${product.id}`);
      return;
    }

    const basePath =
      product.productType === "basket"
        ? `/admin/products/baskets/${product.providerId}/${product.id}`
        : `/admin/products/portfolios/${product.providerId}/${product.id}`;
    navigate(basePath);
  };

  const handleAnalyzeClick = (product: AdminProductItem) => {
    navigate(`/admin/min-analyzer/${product.productType}/${product.providerId}/${product.id}`);
  };

  return (
    <div className="h-full bg-rp-black overflow-auto custom-scrollbar">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-rp-grey">Products</h1>
            <p className="text-xs text-rp-grey/60 mt-0.5">
              All baskets, portfolios, and managed strategies across providers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadCsv}
              disabled={loading || products.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-rp-separator text-rp-grey hover:bg-rp-separator/80 border border-rp-separator transition-colors disabled:opacity-40"
              title="Download metadata as CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={() => setShowCsvUpload(true)}
              disabled={loading || products.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-rp-amber text-black hover:bg-rp-amber/80 transition-colors disabled:opacity-40"
              title="Upload metadata CSV"
            >
              <Upload className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Upload</span>
            </button>
            <button
              onClick={reload}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-rp-separator text-rp-grey hover:bg-rp-separator/80 border border-rp-separator transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        {!loading && !error && (
          <AdminProductStatsHeader
            totalCount={totalCount}
            basketCount={basketCount}
            portfolioCount={portfolioCount}
            managedStrategyCount={managedStrategyCount}
          />
        )}

        {/* Filters */}
        {!loading && !error && (
          <AdminProductFilters
            filterTab={filterTab}
            onFilterTabChange={setFilterTab}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            providerFilter={providerFilter}
            onProviderFilterChange={setProviderFilter}
            visibilityFilter={visibilityFilter}
            onVisibilityFilterChange={setVisibilityFilter}
            remarksFilter={remarksFilter}
            onRemarksFilterChange={setRemarksFilter}
            providers={providers}
          />
        )}

        {/* Active column filters indicator */}
        {activeFilterCount > 0 && (
          <div className="flex items-center justify-end mb-2 gap-2">
            <span className="text-[10px] text-rp-grey/50">
              {activeFilterCount} column filter{activeFilterCount > 1 ? "s" : ""} active
            </span>
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1 text-[10px] text-rp-amber hover:text-rp-amber/80 font-medium"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-rp-grey" />
            <span className="ml-3 text-rp-grey">Loading products...</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 rounded-lg bg-rp-red/20 border border-rp-red">
            <p className="text-rp-red">{error}</p>
            <button
              onClick={reload}
              className="mt-2 px-4 py-2 rounded-lg text-sm font-medium bg-rp-red text-rp-grey hover:bg-rp-red/80"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <AdminProductsTable
            products={columnFilteredProducts}
            sortField={sortField}
            sortDesc={sortDesc}
            onSort={handleSort}
            columnFilters={columnFilters}
            filterOptions={filterOptions}
            onToggleFilter={toggleFilter}
            onSelectAllFilter={selectAllFilter}
            onClearFilter={clearFilter}
            getMetadata={getMetadata}
            isSaving={isSaving}
            isVisibilitySaving={isVisibilitySaving}
            onProductClick={handleProductClick}
            onAnalyzeClick={handleAnalyzeClick}
            onRemarksClick={handleRemarksClick}
            onTagChange={updateTag}
            onVisibilityChange={handleVisibilityChange}
          />
        )}
      </div>

      {showCsvUpload && (
        <CsvUploadReview
          products={products}
          onComplete={handleCsvUploadComplete}
          onCancel={() => setShowCsvUpload(false)}
        />
      )}

      {remarksTarget && (
        <ContractRemarksPopover
          target={remarksTarget}
          onClose={() => setRemarksTarget(null)}
        />
      )}
    </div>
  );
};

const AdminProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const flagshipTab = searchParams.get("tab") === "flagships";
  const tabGroupId = React.useId();
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const selectTab = (flagship: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (flagship) next.set("tab", "flagships");
    else next.delete("tab");
    setSearchParams(next);
  };
  return (
    <div className="h-full overflow-auto bg-gray-50 dark:bg-black transition-colors duration-300">
      <div role="tablist" aria-label="Product management" className="mx-6 mt-4 inline-flex gap-1 rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-0.5">
        {[{ flagship: false, label: "All products" }, { flagship: true, label: "Flagship portfolios" }].map((tab, index) => (
          <button key={tab.label} role="tab" id={`${tabGroupId}-${index}`} aria-controls={`${tabGroupId}-panel`}
            ref={element => { tabRefs.current[index] = element; }}
            aria-selected={flagshipTab === tab.flagship} tabIndex={flagshipTab === tab.flagship ? 0 : -1}
            onClick={() => selectTab(tab.flagship)} onKeyDown={event => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? 1 : 1 - index;
              selectTab(nextIndex === 1);
              tabRefs.current[nextIndex]?.focus();
          }} className={`rounded-md px-4 py-2 text-sm ${flagshipTab === tab.flagship ? "bg-rp-amber text-white" : "text-gray-500 dark:text-gray-400"}`}>
            {tab.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${tabGroupId}-panel`} aria-labelledby={`${tabGroupId}-${flagshipTab ? 1 : 0}`}>
        {flagshipTab ? <FlagshipPortfoliosTab /> : <ProductsCatalog />}
      </div>
    </div>
  );
};

export default AdminProductsPage;
